// ============================================================================
// BP-09-04: Shared Community Components
// ============================================================================

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, MessageSquare, Users, Heart, X } from 'lucide-react';
import { useCommunityStore } from '../stores/communityStore';

// =============================================================================
// CommunitySearch
// =============================================================================

interface CommunitySearchProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  className?: string;
}

export const CommunitySearch: React.FC<CommunitySearchProps> = ({
  placeholder = 'Search community...',
  onSearch,
  className = '',
}) => {
  const { searchQuery, setSearchQuery } = useCommunityStore();

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    onSearch?.(value);
  };

  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-12 pr-10 py-3 rounded-xl border border-gray-200 text-navy placeholder-warm-gray focus:outline-none focus:ring-2 focus:ring-sage/20 focus:border-sage transition-all"
      />
      {searchQuery && (
        <button
          onClick={() => handleSearch('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-warm-gray" />
        </button>
      )}
    </div>
  );
};

// =============================================================================
// CommunityNav
// =============================================================================

const navItems = [
  { path: '/community', label: 'Overview', icon: Users, exact: true },
  { path: '/community/forums', label: 'Forums', icon: MessageSquare },
  { path: '/community/mentors', label: 'Mentors', icon: Heart },
  { path: '/community/groups', label: 'Support Groups', icon: Users },
];

interface CommunityNavProps {
  className?: string;
}

export const CommunityNav: React.FC<CommunityNavProps> = ({ className = '' }) => {
  const location = useLocation();

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <nav className={`flex items-center gap-1 ${className}`}>
      {navItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path, item.exact);

        return (
          <Link
            key={item.path}
            to={item.path}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
              transition-all duration-200
              ${active
                ? 'bg-sage text-white'
                : 'text-navy-light hover:bg-cream hover:text-navy'
              }
            `}
          >
            <Icon className="w-4 h-4" />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
};

export default { CommunitySearch, CommunityNav };



